import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ShieldAlert, ArrowLeft, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/lib/assets";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/wall";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("choose"); // choose | email
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const afterAuth = (user) => {
    setUser(user);
    toast.success("Welcome to the Robbed.");
    navigate(user.role === "admin" ? "/admin" : "/dashboard");
  };

  const emailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      afterAuth(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <div className="text-center">
        <img src={ASSETS.pointing} alt="$ROBBED" className="w-28 mx-auto animate-floaty" />
        <h1 className="headline text-cream text-4xl mt-3">WELCOME TO<br /><span className="text-neon">THE ROBBED</span></h1>
        <p className="font-mono text-dim text-sm mt-3">No suits. No banks. Just memes.</p>
      </div>

      <div className="comic-card p-6 mt-8 space-y-3">
        {mode === "choose" ? (
          <>
            <button onClick={googleLogin} className="btn-neon w-full" data-testid="login-google">Continue With Google</button>
            <button onClick={() => setMode("email")} className="btn-ghost w-full" data-testid="login-email-toggle">
              <Mail className="w-4 h-4" /> Continue With Email
            </button>
            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-[3px] bg-charcoal" />
              <span className="font-mono text-[10px] text-dim uppercase">wallet sign-in</span>
              <div className="flex-1 h-[3px] bg-charcoal" />
            </div>
            <button type="button" disabled title="Wallet connection will be available when $ROBBED launches."
              className="btn-ghost w-full !border-charcoal !text-dim opacity-60 cursor-not-allowed !text-sm whitespace-nowrap" data-testid="login-wallet-disabled">
              <Lock className="w-4 h-4 shrink-0" /> Connect Wallet
            </button>
            <p className="font-mono text-[10px] text-dim text-center">Available when $ROBBED launches</p>
          </>
        ) : (
          <form onSubmit={emailLogin} className="space-y-3">
            <button type="button" onClick={() => setMode("choose")} className="flex items-center gap-1 font-mono text-xs text-dim hover:text-neon" data-testid="login-back">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <input data-testid="login-email" type="email" required placeholder="Email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-ink border-3 border-black px-3 py-2.5 font-mono text-sm text-cream focus:outline-none focus:border-neon" />
            <input data-testid="login-password" type="password" required placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-ink border-3 border-black px-3 py-2.5 font-mono text-sm text-cream focus:outline-none focus:border-neon" />
            <button type="submit" disabled={loading} className="btn-neon w-full disabled:opacity-50" data-testid="login-submit">
              {loading ? "..." : "Log In"}
            </button>
            <div className="flex justify-between font-mono text-xs">
              <Link to="/forgot-password" className="text-dim hover:text-neon" data-testid="link-forgot">Forgot password?</Link>
              <Link to="/register" className="text-neon hover:underline" data-testid="link-register">Create account</Link>
            </div>
          </form>
        )}
      </div>

      <div className="flex items-start gap-3 mt-5 comic-card p-4 bg-[#0f1a0f]">
        <ShieldAlert className="w-6 h-6 text-neon shrink-0" />
        <p className="font-mono text-[11px] text-dim leading-relaxed">
          ROBBED will never ask for your password over email or DMs. Wallet sign-in arrives when $ROBBED
          launches — and even then, signing only proves ownership, it never moves your funds.
        </p>
      </div>
    </div>
  );
}
